<script lang="ts" setup>
import { useModal } from 'vue-final-modal'

import BasicButton from '~/components/form-elements/BasicButton.vue'
import Auth from '~/components/modal-windows/Auth.vue'
import constants from '~/constants'
import AuthAction from '~/enums/auth-action'
import Page from '~/enums/page'
import helpers from '~/helpers'
import formatter from '~/helpers/formatter'
import { useAuthStore } from '~/store/auth'
import { useCartStore } from '~/store/cart'

withDefaults(
  defineProps<{
    isCheckout?: boolean
  }>(),
  { isCheckout: false }
)

const authStore = useAuthStore()
const cartStore = useCartStore()

const showAmountWithPromoCode = computed(
  () => !cartStore.isPromoCodeApplied
    && totalWithPromoCodeInKopecks.value !== null
    && totalWithPromoCodeInKopecks.value < cartStore.totalInKopecks
)
const totalWithPromoCodeInKopecks = computed(() => {
  let sum = 0

  for (const { attributes, meta } of cartStore.cartProductsData) {
    const { priceWithPromoCode } = attributes

    if (typeof priceWithPromoCode === 'number') {
      sum += meta.quantity! * (helpers.convertRublesToKopecks(priceWithPromoCode) ?? 0)
    } else {
      return null
    }
  }

  return sum
    + (cartStore.finalShippingCostInKopecks ?? 0)
    + (cartStore.isUrgentOrder && cartStore.urgentOrderPriceInKopecks !== null ? cartStore.urgentOrderPriceInKopecks : 0)
})

async function openAuthModalWindow (): Promise<void> {
  await useModal({ component: Auth, attrs: { action: AuthAction.SignIn } }).open()
}
</script>

<template>
  <div>
    <table class="mb-4">
      <tbody>
        <tr>
          <th>
            Итого
          </th>
          <td>
            <span class="!text-lg !font-medium">{{ formatter.formatPrice(cartStore.totalInKopecks, true) }}&nbsp;₽</span>
          </td>
        </tr>

        <tr>
          <th>
            <span>
              {{ cartStore.totalItems }}&nbsp;{{ helpers.getDeclension(cartStore.totalItems, constants.wordForms.product) }}
            </span><span v-if="cartStore.weightInGrams! > 0">, ~{{ cartStore.weightInGrams }}&nbsp;г</span>
          </th>
          <td class="opacity-[.4]">
            <template v-if="cartStore.discountAmountInKopecks">
              {{ formatter.formatPrice(cartStore.subtotalWithDiscountInKopecks, true) }}&nbsp;₽<br>
              <del>{{ formatter.formatPrice(cartStore.subtotalInKopecks, true) }}&nbsp;₽</del>
            </template>
            <template v-else>
              {{ formatter.formatPrice(cartStore.subtotalWithDiscountInKopecks, true) }}&nbsp;₽
            </template>
          </td>
        </tr>

        <tr v-if="cartStore.discountAmountInKopecks">
          <th>
            Скидка
          </th>
          <td class="!text-main-green">
            −{{ formatter.formatPrice(cartStore.discountAmountInKopecks, true) }}&nbsp;₽
          </td>
        </tr>

        <tr v-if="cartStore.isUrgentOrder">
          <th>
            Срочная сборка
          </th>
          <td>
            <span class="font-medium">
              {{ formatter.formatPrice(cartStore.urgentOrderPriceInKopecks, true) }}&nbsp;₽
            </span>
          </td>
        </tr>

        <template v-if="cartStore.shippingCostInKopecks !== null">
          <tr>
            <th>
              Доставка
            </th>
            <td>
              <template v-if="cartStore.isFreeShippingEnabled">
                <template v-if="cartStore.shippingCostInKopecks === 0">
                  0&nbsp;₽
                </template>
                <template v-else>
                  0&nbsp;₽<br>
                  <del>{{ formatter.formatPrice(cartStore.shippingCostInKopecks, true) }}&nbsp;₽</del>
                </template>
              </template>
              <template v-else-if="cartStore.fixedShippingCostInKopecks !== null">
                <template v-if="cartStore.fixedShippingCostInKopecks === cartStore.shippingCostInKopecks">
                  {{ formatter.formatPrice(cartStore.fixedShippingCostInKopecks, true) }}&nbsp;₽
                </template>
                <template v-else>
                  {{ formatter.formatPrice(cartStore.fixedShippingCostInKopecks, true) }}&nbsp;₽<br>
                  <del>{{ formatter.formatPrice(cartStore.shippingCostInKopecks, true) }}&nbsp;₽</del>
                </template>
              </template>
              <template v-else>
                {{ formatter.formatPrice(cartStore.shippingCostInKopecks, true) }}&nbsp;₽
              </template>
            </td>
          </tr>

          <tr v-if="showAmountWithPromoCode">
            <th>
              К&nbsp;оплате с&nbsp;промокодом
            </th>
            <td>
              <AmountWithPromoCode :total-with-promo-code-in-kopecks="totalWithPromoCodeInKopecks as number" />
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <BasicButton
      v-if="!isCheckout"
      id="cart-next-button"
      class="my-4 !h-[56px] w-full"
      color="green"
      :title="'Перейти к\xA0оформлению'"
      @click="navigateTo(Page.Checkout)"
    />

    <div class="mb-2 h-px w-full bg-main-gray-low" />

    <table>
      <tbody>
        <template v-if="authStore.isUser">
          <tr v-if="cartStore.cashbackInPoints !== null && cartStore.cashbackInPoints !== 0">
            <th>
              Баллы iTAB Плюс
            </th>
            <td>
              <div class="flex items-center justify-end">
                <SvgIcon
                  class="!text-2xl font-medium text-main-green"
                  name="outlined/itab-reverse"
                />

                <span class="ml-1 inline-block">
                  {{ formatter.formatPrice(cartStore.cashbackInPoints) }}
                </span>
              </div>
            </td>
          </tr>

          <tr v-if="cartStore.cashbackInSuperminsPoints !== null && cartStore.cashbackInSuperminsPoints !== 0">
            <th>
              Баллы Supermins
            </th>
            <td>
              <div class="flex items-center justify-end">
                <SvgIcon
                  class="!text-2xl font-medium text-[#FFB524]"
                  name="outlined/supermins"
                />

                <span class="ml-1 inline-block">
                  {{ formatter.formatPrice(cartStore.cashbackInSuperminsPoints) }}
                </span>
              </div>
            </td>
          </tr>
        </template>

        <tr v-if="cartStore.goldenTicketsCount">
          <th>
            Золотые билеты
          </th>
          <td>
            <GoldenTickets :golden-tickets-count="cartStore.goldenTicketsCount" />
          </td>
        </tr>

        <tr v-if="cartStore.backToSchoolTicketsCount">
          <th>
            Билеты #Back2school
          </th>
          <td>
            <BackToSchoolTickets :back-to-school-tickets-count="cartStore.backToSchoolTicketsCount" />
          </td>
        </tr>

        <template v-if="!authStore.isUser">
          <th class="text-sm text-main-dark">
            <a class="text-main-green" href="#" @click.prevent="openAuthModalWindow">Войдите или зарегистрируйтесь</a>, чтобы получить баллы
          </th>
        </template>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" scoped>
table {
  tbody {
    tr {
      border: 0;
      display: table-row;
      line-height: normal !important;

      td, th {
        color: #434242;
        font-size: 16px;
        padding: 8px 0;
      }

      td {
        text-align: right !important;
        white-space: nowrap;
      }
    }
  }
}
</style>
